import React, {useMemo, useRef, useLayoutEffect, useState, useCallback} from 'react';
import {max as lodashMax, map, min as lodashMin, range as lodashRange, floor, ceil} from 'lodash';
import theme from '../../theme';
import {makeStyles} from '@material-ui/styles';

const useStyles = makeStyles({
    label: {userSelect: 'none'}
});

export interface LineChartProps extends React.SVGProps<SVGSVGElement> {
    data: [number, number | undefined][];
    dataColor?: string;
    hoverX?: number;
    width: number;
    height: number;
    xUnits: string;
    yUnits: string;
    initialXMax?: number;
    initialXMin?: number;
    initialYMax?: number;
    initialYMin?: number;
    onLineChartMouseMove?: (event: {dataX: number; dataY: number} | undefined) => void;
}

export default function LineChart({
    xUnits,
    yUnits,
    data,
    width,
    height,
    initialXMax = 1,
    initialXMin = 0,
    initialYMax = 1,
    initialYMin = 0,
    onLineChartMouseMove,
    onMouseLeave: onMouseLeaveProp,
    dataColor,
    hoverX: hoverXProp,
    ...props
}: LineChartProps) {

    const classes = useStyles();

    const {dataXMax, dataYMax, dataXMin, dataYMin} = useMemo(() => ({
        dataXMax: lodashMax(map(data, '[0]')) || initialXMax,
        dataYMax: lodashMax(map(data, '[1]')) || initialYMax,
        dataXMin: lodashMin(map(data, '[0]')) || initialXMin,
        dataYMin: lodashMin(map(data, '[1]')) || initialYMin,
    }), [data, initialXMax, initialXMin, initialYMax, initialYMin]);

    const [axisLabelHeight, setAxisLabelHeight] = useState<number>(0);
    const [yAxisWidth, setYAxisWidth] = useState<number>(0);
    const [xAxisRightPadding, setXAxisRightPadding] = useState<number>(0);
    const [yAxisTopPadding, setYAxisTopPadding] = useState<number>(0);
    const [hoverXState, setHoverXState] = useState<number | undefined>();
    const hoverX = hoverXProp !== undefined ? hoverXProp : hoverXState;

    const ref = useRef<SVGSVGElement>(null);
    const yAxisRef = useRef<SVGGElement>(null);
    const xAxisRef = useRef<SVGGElement>(null);

    useLayoutEffect(() => {
        if (xAxisRef.current && yAxisRef.current) {
            setAxisLabelHeight(xAxisRef.current.getBBox().height);
            setYAxisWidth(yAxisRef.current.getBBox().width);

            const xAxisLastLabel = xAxisRef.current.lastChild as SVGTextElement;
            if (xAxisLastLabel) setXAxisRightPadding(xAxisLastLabel.getBBox().width / 2);

            const yAxisLastLabel = yAxisRef.current.lastChild as SVGTextElement;
            if (yAxisLastLabel) setYAxisTopPadding(yAxisLastLabel.getBBox().height / 2);
        }
    }, [dataXMax, dataYMax, dataXMin, dataYMin]);

    const paddingBottom = theme.spacing(1);
    const paddingLeft = theme.spacing(1);

    const bodyWidth = width - yAxisWidth - paddingLeft - xAxisRightPadding;
    const bodyXOffset = yAxisWidth + paddingLeft;
    const bodyHeight = height - axisLabelHeight - paddingBottom - yAxisTopPadding;
    const bodyYOffset = yAxisTopPadding;

    const yAxisLabels = getLabels(dataYMin, dataYMax, 5);
    const xAxisLabels = getLabels(dataXMin, dataXMax, 5);
    const xAxisMin = xAxisLabels[0];
    const yAxisMin = yAxisLabels[0];
    const yAxisMax = yAxisLabels[yAxisLabels.length - 1];

    const project = useCallback((x: number, y: number): [number, number] => [
        bodyXOffset + (x - xAxisMin) / (dataXMax - xAxisMin) * bodyWidth,
        bodyYOffset + bodyHeight - (y - yAxisMin) / (yAxisMax - yAxisMin) * bodyHeight
    ], [bodyHeight, bodyWidth, bodyXOffset, bodyYOffset, dataXMax, xAxisMin, yAxisMax, yAxisMin]);

    const unproject = useCallback((x: number, y: number): [number, number] => [
        (x - bodyXOffset) * (dataXMax - xAxisMin) / bodyWidth + xAxisMin,
        (bodyHeight + bodyYOffset - y) * (yAxisMax - yAxisMin) / bodyHeight + yAxisMin
    ], [bodyHeight, bodyWidth, bodyXOffset, bodyYOffset, dataXMax, xAxisMin, yAxisMax, yAxisMin]);

    const onMouseMove = useCallback(event => {
        const rect = ref.current && ref.current.getBoundingClientRect();
        if (!rect) return;
        const [dataX, dataY] = unproject(event.clientX - rect.left, event.clientY - rect.top);
        if (dataX >= dataXMin && dataX <= dataXMax) {
            setHoverXState(dataX);
            if (onLineChartMouseMove) onLineChartMouseMove({dataX, dataY});
        } else {
            setHoverXState(undefined);
            if (onLineChartMouseMove) onLineChartMouseMove(undefined);
        }
    }, [dataXMax, dataXMin, onLineChartMouseMove, unproject]);

    const onMouseLeave = useCallback(event => {
        setHoverXState(undefined);
        if (onMouseLeaveProp) onMouseLeaveProp(event);
    }, [onMouseLeaveProp]);

    const hoverY = hoverX && interpolate(data, hoverX);

    return <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        ref={ref}
        {...props}
    >
        <g ref={yAxisRef}>{yAxisLabels.map((dataY, index) =>
            <text
                fontSize={theme.typography.caption.fontSize}
                fill="currentColor"
                key={index}
                x={yAxisWidth}
                y={project(0, dataY)[1] + (axisLabelHeight / 3)}
                textAnchor="end"
                opacity={hoverX !== undefined ? 0.2 : 0.8}
                className={classes.label}
            >
                {dataY.toLocaleString()}
                {' '}
                {yUnits}
            </text>
        )}</g>

        <g>{yAxisLabels.map((dataY, index) =>
            <line
                x1={bodyXOffset}
                x2={bodyXOffset + bodyWidth}
                y1={project(0, dataY)[1]}
                y2={project(0, dataY)[1]}
                stroke="currentColor"
                opacity="0.2"
                strokeWidth="1"
                key={index}
            />
        )}</g>

        <g ref={xAxisRef}>{xAxisLabels.slice(0, -1).map((dataX, index) =>
            <text
                fontSize={theme.typography.caption.fontSize}
                fill="currentColor"
                key={index}
                y={height - paddingBottom + axisLabelHeight / 3}
                x={project(dataX, 0)[0]}
                textAnchor="middle"
                opacity={hoverX !== undefined ? 0.2 : 0.8}
                className={classes.label}
            >
                {dataX.toLocaleString()}
                {' '}
                {xUnits}
            </text>
        )}</g>

        <g>{xAxisLabels.slice(0, -1).map((dataX, index) =>
            <line
                x1={project(dataX, 0)[0]}
                x2={project(dataX, 0)[0]}
                y1={bodyYOffset}
                y2={bodyYOffset + bodyHeight}
                stroke="currentColor"
                opacity="0.2"
                strokeWidth="1"
                key={index}
            />
        )}</g>

        <line
            x1={project(dataXMax, 0)[0]}
            x2={project(dataXMax, 0)[0]}
            y1={bodyYOffset}
            y2={bodyYOffset + bodyHeight}
            stroke="currentColor"
            opacity="0.2"
            strokeWidth="1"
        />

        <path
            stroke={dataColor || theme.palette.primary.main}
            strokeLinecap="round"
            strokeWidth="2"
            fill="none"
            d={data.map(([dataX, dataY], index) =>
                dataY === undefined ?
                    '' :
                    [
                        index === 0 || data[index - 1][1] === undefined ? 'M' : 'L',
                        Math.round(project(dataX, dataY)[0]),
                        Math.round(project(dataX, dataY)[1]),
                    ].join(' ')
            ).join(' ')}
        />

        {hoverX !== undefined && <line
            x1={project(hoverX, 0)[0]}
            x2={project(hoverX, 0)[0]}
            y1={bodyYOffset}
            y2={bodyYOffset + bodyHeight}
            stroke={theme.palette.secondary.main}
            opacity="0.7"
            strokeWidth="1"
        />}

        {hoverX !== undefined && <text
            fontSize={theme.typography.caption.fontSize}
            fill="currentColor"
            y={height - paddingBottom + axisLabelHeight / 3}
            x={project(hoverX, 0)[0]}
            textAnchor="middle"
            opacity="0.8"
            className={classes.label}
        >
            {precisionRound(hoverX, 2).toLocaleString()}
            {' '}
            {xUnits}
        </text>}

        {hoverY !== undefined && <line
            x1={bodyXOffset}
            x2={bodyXOffset + bodyWidth}
            y1={project(0, hoverY)[1]}
            y2={project(0, hoverY)[1]}
            stroke={theme.palette.secondary.main}
            opacity="0.7"
            strokeWidth="1"
        />}

        {hoverY !== undefined && <text
            fontSize={theme.typography.caption.fontSize}
            fill="currentColor"
            x={yAxisWidth}
            y={project(0, hoverY)[1] + (axisLabelHeight / 3)}
            textAnchor="end"
            opacity="0.8"
            className={classes.label}
        >
            {precisionRound(hoverY, 2).toLocaleString()}
            {' '}
            {yUnits}
        </text>}
    </svg>;
}

function getLabels(min: number, max: number, count: number) {
    const range = max - min;
    const rangeLog = Math.round(Math.log(range) / Math.log(10));
    const intervalLog = (range * Math.pow(10, -rangeLog)) < count ? rangeLog - 1 : rangeLog;
    const intervalPow = Math.pow(10, intervalLog);
    const interval = Math.ceil(range / intervalPow / count) * intervalPow;

    return lodashRange(
        floor(min, -intervalLog),
        ceil(max + interval, -intervalLog),
        interval
    );
}

function interpolate(data: [number, number | undefined][], x: number) {
    let lastY;
    for (let i = 0; i < data.length; i++) {
        const datum = data[i];
        const [x1, y1] = datum;
        if (y1 === undefined) continue;
        lastY = y1;
        if (x1 < x) continue;
        if (i === 0) return y1;
        const prevDatum = data[i - 1];
        const [x0, y0] = prevDatum;
        if (y0 === undefined) return y1;
        // https://en.wikipedia.org/wiki/Linear_interpolation
        return y0 + (x - x0) * (y1 - y0) / (x1 - x0);
    }
    return lastY;
}

function precisionRound(input: number, sigfigs: number) {
    return parseFloat(input.toPrecision(sigfigs));
}
